/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  AmountJson,
  TalerError,
  TranslatedString,
} from "@gnu-taler/taler-util";
import { Loading, RouteDefinition, utils } from "@gnu-taler/web-util/browser";
import { VNode } from "preact";

import { LoginForm } from "../LoginForm.js";
import { useComponentState } from "./state.js";
import { RetryableError } from "../../components/RetryableError.js";
import { ReadyView } from "./views.js";

export interface Props {
  account: string;

  routeCashout: RouteDefinition;
  routeChargeWallet: RouteDefinition;
  routeWireTransfer: RouteDefinition;
  routeTransactions: RouteDefinition;
  routeAccountDetails: RouteDefinition;
  routeOperationDetails: RouteDefinition<{ wopid: string }>;
}

export type State =
  | State.Loading
  | State.LoadingError
  | State.Ready
  | State.UserNotFound;

export namespace State {
  export interface Loading {
    status: "loading";
    error: undefined;
  }

  export interface LoadingError {
    status: "loading-error";
    error: TalerError;
    title: TranslatedString;
    onRetry(): void;
  }

  export interface BaseInfo {
    error: undefined;
  }

  export interface Ready extends BaseInfo {
    status: "ready";
    error: undefined;
    account: string;
    accountPaytoUri: string;
    accountLegalName: string;
    accountLoginName: string;
    accountBankHost?: string;
    balance: AmountJson;
    balanceIsDebit: boolean;
    routeCashout: RouteDefinition;
    routeChargeWallet: RouteDefinition;
    routeWireTransfer: RouteDefinition;
    routeTransactions: RouteDefinition;
    routeAccountDetails: RouteDefinition;
    showRecentActivity?: boolean;
    routeOperationDetails: RouteDefinition<{ wopid: string }>;
  }

  export interface UserNotFound {
    status: "login";
    reason: "not-found" | "forbidden";
    routeRegister?: RouteDefinition;
  }
}

const viewMapping: utils.StateViewMap<State> = {
  loading: Loading,
  login: LoginForm,
  "loading-error": RetryableError,
  ready: ReadyView,
};

export const AccountPage: (p: Props) => VNode = utils.compose(
  (p: Props) => useComponentState(p),
  viewMapping,
);
